import {
  Textarea as MantineTextarea,
  TextareaProps as MantineTextareaProps,
  rem,
} from '@mantine/core';
import clsx from 'clsx';
import { CSSProperties, forwardRef } from 'react';

import styles from './Textarea.module.css';

export interface TextareaProps extends Omit<
  MantineTextareaProps,
  | 'variant'
  | 'color'
  | 'styles'
  | 'classNames'
  | 'style'
  | 'size'
  | 'value'
  | 'readOnly'
> {
  /** Add 'fs-mask' class for FullStory masking of sensitive data */
  isMasked?: boolean;
  isReadOnly?: boolean;
  /** Current value (for controlled component with character counter) */
  value?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      disabled,
      isReadOnly,
      isMasked = false,
      value,
      maxLength,
      rightSection,
      rightSectionWidth,
      ...props
    },
    ref
  ) => {
    // Mantine's `unstyled` mode strips its built-in CSS, including the logic
    // that sizes sections and adjusts input padding based on rightSectionWidth.
    // We bridge the gap by setting a CSS custom property on the wrapper, which
    // our Textarea.module.css reads to set both the section width and the
    // matching input padding (falling back to a sensible default).
    const sectionWidthVars: Record<string, string> = {};
    if (rightSectionWidth !== undefined && rightSectionWidth !== null) {
      sectionWidthVars['--right-section-width'] = rem(rightSectionWidth);
    }

    const textareaElement = (
      <MantineTextarea
        ref={ref}
        unstyled
        classNames={{
          root: styles.root,
          wrapper: styles.wrapper,
          input: clsx(styles.input, isMasked && 'fs-mask'),
          section: styles.section,
          label: styles.label,
          error: styles.error,
          required: styles.required,
          description: styles.description,
        }}
        wrapperProps={{
          style: sectionWidthVars as CSSProperties,
        }}
        label={label}
        error={error}
        disabled={disabled}
        readOnly={isReadOnly}
        value={value}
        maxLength={maxLength}
        rightSection={rightSection}
        rightSectionWidth={rightSectionWidth}
        {...props}
      />
    );

    // When maxLength is provided, wrap with container for counter positioning
    if (maxLength) {
      return (
        <div className={styles.container}>
          {textareaElement}
          <span className={styles.counter}>
            {value?.length ?? 0}/{maxLength}
          </span>
        </div>
      );
    }

    return textareaElement;
  }
);

Textarea.displayName = 'Textarea';
